export const SPECIALITIES = [
  { id: "barrel_aged", name: "Barrel-Aged Coffee", note: "Aged in wine, whisky & rum barrels." },
  { id: "fermented", name: "Fermented Coffee", note: "Experimental microlots, unique profiles." },
  { id: "arabica", name: "100% Arabica", note: "Smooth, aromatic, classic specialty." },
  { id: "robusta", name: "100% Robusta", note: "Bold fermented microlots, extra caffeine kick." },
  { id: "blended", name: "Blended (80:20)", note: "Classic Arabica-Robusta day coffees." },
  { id: "custom_blend", name: "Create Your Own Blend", note: "Mix your own Arabica : Robusta ratio." },
  { id: "aaa_arabica", name: "AAA Arabica", note: "Top screen-size, premium grade Arabica lots." },
];

export const ROASTS = [
  { id: "light", name: "Light", note: "Bright, floral, higher acidity." },
  { id: "medium", name: "Medium", note: "Balanced body, caramel notes." },
  { id: "dark", name: "Dark", note: "Bold, smoky, low acidity." },
];

export const ROAST_LIMIT = {
  barrel_aged: ["light", "medium"],
  fermented: ["light", "medium"],
};

export const GRINDS = [
  { id: "whole", name: "Whole Bean" },
  { id: "coarse", name: "Coarse (French Press)" },
  { id: "cold_brew", name: "Cold Brew" },
  { id: "aeropress", name: "Aeropress" },
  { id: "moka_pot", name: "Moka Pot" },
  { id: "south_indian_filter", name: "South Indian Filter" },
  { id: "pourover", name: "Pourover" },
  { id: "fine", name: "Espresso (Fine)" },
];

export const PRICES_INR = {
  barrel_aged: 708.75,
  fermented: 630,
  arabica: 472.5,
  robusta: 603.75,
  blended: 420,
  aaa_arabica: 550,
};

export const QUANTITIES = [
  { id: "250", label: "250 g", mult: 1, grams: 250 },
  { id: "500", label: "500 g", mult: 2, grams: 500 },
  { id: "1000", label: "1 kg", mult: 4, grams: 1000 },
  { id: "5000", label: "5 kg", mult: 20, grams: 5000 },
  { id: "10000", label: "10 kg", mult: 40, grams: 10000 },
  { id: "60000", label: "60 kg Green Bag", mult: 240, grams: 60000, quoteOnly: true },
];

export const SIZE_LABELS = Object.fromEntries(QUANTITIES.map((q) => [q.id, q.label]));

export function basePriceInr(speciality, blendPct) {
  if (speciality === "custom_blend") {
    const pct = Math.max(0, Math.min(100, blendPct ?? 70));
    return (PRICES_INR.arabica * pct + PRICES_INR.robusta * (100 - pct)) / 100;
  }
  return PRICES_INR[speciality];
}

export function unitPriceInr(speciality, sizeId, blendPct) {
  const mult = QUANTITIES.find((q) => q.id === String(sizeId))?.mult ?? 1;
  return Math.round(basePriceInr(speciality, blendPct) * mult * 100) / 100;
}

export function cartShippingInr(zone, totalGrams) {
  if (zone === "india") return totalGrams <= 1000 ? 385 : 657 * Math.max(1, Math.ceil(totalGrams / 5000));
  const brackets = [[250, 800], [500, 1200], [1000, 2000], [5000, 7500], [10000, 13000], [60000, 60000]];
  for (const [g, p] of brackets) if (totalGrams <= g) return p;
  return 60000;
}

const barrelCommon = { beans: "Arabica", process: "Washed", grade: "B+ Clean Bulk", varietal: "Selection 9", best: "Manual Brews" };
const fermentedCommon = { beans: "Arabica", grade: "B+ Clean Bulk", best: "Manual Brews" };
const robustaCommon = { beans: "Robusta", grade: "B+ Clean Bulk", varietal: "Old Peridinia", best: "Manual Brews" };
const arabicaCommon = { beans: "100% Arabica", process: "Washed", grade: "Assorted Blend" };
const blendedCommon = { composition: "80:20 Blend", process: "Washed", grade: "Assorted Blend" };

export const VARIANTS = {
  barrel_aged: [
    { id: "wine_barrel", name: "Wine Barrel-Aged", ...barrelCommon, notes: "Sweet Orange, Blackberry Jam, Wood Apple", cupping: 85 },
    { id: "whisky_barrel", name: "Whisky Barrel-Aged", ...barrelCommon, notes: "Irish Cream, Green Apple and Tropical Fruits", cupping: 86 },
    { id: "rum_barrel", name: "Rum Barrel-Aged", ...barrelCommon, notes: "Red Apple, Blueberry, Rum & Raisins", cupping: 87 },
    { id: "two_indies_rum", name: "2 Indies Rum", ...barrelCommon, notes: "Sweet Raisins, Dark Chocolate & Orange Blossoms", cupping: 85 },
  ],
  fermented: [
    { id: "banana_black_honey", name: "Banana Black Honey", ...fermentedCommon, process: "PSD", varietal: "Chandragiri", notes: "Overripe Banana, Mango, Amarula", cupping: 83 },
    { id: "yellow_honey", name: "Yellow Honey", ...fermentedCommon, process: "PSD", varietal: "Selection 9 & Chandragiri", notes: "Sweet Orange, Chocolate, Floral and Caramel", cupping: 85 },
    { id: "red_honey", name: "Red Honey", ...fermentedCommon, process: "PSD", varietal: "Selection 9 & Chandragiri", notes: "Mulberry (dark berries), Cherry, Jaggery", cupping: 82 },
    { id: "black_honey", name: "Black Honey", ...fermentedCommon, process: "PSD", varietal: "Selection 9 & Chandragiri", notes: "Caramelised Overripe Banana, Hint of Dark Berries and Tropical Fruits", cupping: 84 },
    { id: "banana_washed", name: "Banana Washed", ...fermentedCommon, process: "Washed", varietal: "Chandragiri", notes: "Sweet Custard Cream, Banana, Ruby Grapefruit", cupping: 83.5 },
    { id: "grappa", name: "Grappa", ...fermentedCommon, process: "Cherry", varietal: "Selection 9", notes: "Jackfruit (biluva), Raisins, Fig, Sweet Citron and Brandy", cupping: 86 },
    { id: "pichia", name: "Pichia", ...fermentedCommon, process: "Cherry", varietal: "Selection 9", notes: "Sweet Citrus and Floral", cupping: 88.5 },
    { id: "wetplum", name: "Wetplum", ...fermentedCommon, process: "Cherry", varietal: "Selection 9", notes: "Sweet Nectarine, Mixed Berries and Jammy", cupping: 90 },
    { id: "natural_maceration", name: "Natural Maceration", ...fermentedCommon, process: "Cherry", varietal: "Chandragiri & Cauvery", notes: "Berry Jam, Dark Chocolate, Candied Hazelnut", cupping: 86 },
    { id: "yeast_maceration", name: "Yeast Maceration", ...fermentedCommon, process: "Cherry", varietal: "Chandragiri & Cauvery", notes: "Winey, Green Apple, Cherry and Dark Chocolate", cupping: 89.5 },
    { id: "nitrogen_maceration", name: "Nitrogen Maceration", ...fermentedCommon, process: "Cherry", varietal: "Chandragiri & Cauvery", notes: "Dark Fruits, Cherry, Raisins, Cashew Apple", cupping: 90.5 },
    { id: "fusion_ferment", name: "Fusion Ferment", ...fermentedCommon, process: "Cherry", varietal: "Selection 9", notes: "Red Berries, Raisin and Orange Blossom", cupping: 92 },
    { id: "sugarcane", name: "Sugarcane", ...fermentedCommon, process: "Cherry", varietal: "Chandragiri", notes: "Sugarcane, Ripe Berries and Delicate Floral Finish", cupping: 88 },
    { id: "vintage_24", name: "Vintage 24", ...fermentedCommon, process: "Cherry", varietal: "Selection 9", notes: "Floral Bouquet, Sweet Orange and Peach", cupping: 89 },
    { id: "citrus_reverie", name: "Citrus Reverie", ...fermentedCommon, process: "Washed", varietal: "Chandragiri", notes: "Orange Blossom, Sweet Citron and Sugarcane", cupping: 82.75 },
    { id: "harmonia", name: "Harmonia", ...fermentedCommon, process: "Washed", varietal: "Chandragiri", notes: "Jasmine, Jaggery, Green Grapes", cupping: 86 },
    { id: "golden_jack", name: "Golden Jack", ...fermentedCommon, process: "Cherry", varietal: "Selection 9", notes: "Jackfruits, Ripe Papaya, Dark Cherry Jam", cupping: 84 },
    { id: "golden_orchard", name: "Golden Orchard", ...fermentedCommon, process: "Cherry", varietal: "Selection 9", notes: "Orange Blossom, Apple, Pear", cupping: 87 },
    { id: "sacred_ficus", name: "Sacred Ficus", ...fermentedCommon, process: "Washed", varietal: "Chandragiri", notes: "Fig, Nectar, Apricot, Panela", cupping: 83 },
    { id: "cremeva", name: "Cremeva", ...fermentedCommon, process: "Cherry", varietal: "Selection 9", notes: "Nectarine Wine and Ripe Banana", cupping: 84 },
    { id: "origine_vintage_25", name: "Origine (Vintage 25)", ...fermentedCommon, process: "Washed", varietal: "Chandragiri", notes: "Sweet Nectar, Palm Sugar and Persimmon", cupping: 85 },
  ],
  robusta: [
    { id: "pink_berry_bounce", name: "Pink Berry Bounce", ...robustaCommon, process: "Cherry", notes: "Berry, Peach, Hint of Floral", cupping: 86 },
    { id: "yellow_thunderoof", name: "Yellow Thunderoof", ...robustaCommon, process: "PSD", notes: "Dark Caramel, Roasted Hazelnut", cupping: 87.25 },
    { id: "sugar_glider", name: "Sugar Glider", ...robustaCommon, process: "PSD", notes: "Dark Caramel, Mixed Nuts and Toffee", cupping: 85 },
    { id: "makbrew", name: "Makbrew", ...robustaCommon, process: "Cherry", notes: "Red Plum, Banana Cinnamon, Chocolate and Roasted Hazelnut", cupping: 86 },
    { id: "vintage_24", name: "Vintage 24", ...robustaCommon, process: "Cherry", notes: "Pink Rose, Cherry, Dark Chocolate and Jaggery", cupping: 86 },
  ],
  arabica: [
    { id: "blaze", name: "Blaze", ...arabicaCommon, best: "Milk", notes: "Bitter Cocoa, Nutty Fragrance" },
    { id: "matinee", name: "Matinee", ...arabicaCommon, best: "Black", notes: "Sweet Citrus, Mildly Fruity (Medium Roast)" },
    { id: "sundowner", name: "Sundowner", ...arabicaCommon, best: "Milk", notes: "Sweet Milk Chocolate, Smooth (Medium Roast)" },
    { id: "afterhours", name: "Afterhours", ...arabicaCommon, best: "Black", notes: "Hints of Spice, Citrus (City Roast)" },
    { id: "mountain", name: "Mountain", ...arabicaCommon, best: "Black / Milk", notes: "Berries, Chocolate, Hints of Spices and Fruits" },
  ],
  blended: [
    { id: "pride", name: "Pride", ...blendedCommon, best: "Milk / Black", notes: "Chocolate, Caramel, Nuts and Malt" },
    { id: "indian_filter", name: "Indian Filter", ...blendedCommon, best: "Milk", notes: "Chocolate and Malt" },
    { id: "espresso", name: "Espresso", ...blendedCommon, best: "Black", notes: "Bittersweet Chocolate, Cherry, Hazelnut" },
    { id: "classic_bistro", name: "Classic Bistro", ...blendedCommon, best: "Black / Milk", notes: "Raisins, Citrus Notes, and Milk Chocolate" },
  ],
};

export function variantName(specialityId, variantId) {
  if (!variantId) return null;
  return VARIANTS[specialityId]?.find((v) => v.id === variantId)?.name || variantId.replace(/_/g, " ");
}

export function specialityName(specialityId) {
  return SPECIALITIES.find((s) => s.id === specialityId)?.name || specialityId;
}
